import toggleOS from "src/sdk/toggleOS";
import fillStore from "src/store";
import { getDeal } from "src/store/dataService/actions/deal";
import { initialState as onlineShopperInitialState } from "src/store/reducers/onlineShopper";
import { getPageType } from "src/utils/tracking/autoNationTag";
import getMagicLinkValues from "src/utils/getMagicLinkValues";
import handleHostTrackingData, {
    ASYNC_TRACKERS,
    getStaticTrackingValues,
    PROMISE_TRACKING_VALUES,
} from "src/utils/handleHostTrackingData";
import { getChatLocalState, getLocalDealState, rememberDealFromMagicLink } from "src/utils/localStorage";
import { initBugsnag } from "src/utils/logger";
import render from "src/utils/render";
import { loadLocaleData } from "src/utils/updateLanguage";
import { initialMessageState } from "src/components/Chat/redux/reducer";
import { Environment } from "Types/Environment";
import { datadogRum, RumEvent } from "@datadog/browser-rum-slim";

const setDrawerState = (initialEntries: [string] | undefined) =>
    initialEntries && initialEntries.includes("/my-garage/message-center") ? { isDrawerOpen: true } : null;

const initialize = async (accountSlug: string, env: Environment, srpLink: string) => {
    // Lazy load locale data (translations)
    await loadLocaleData();

    // Look for any magic link values, save to local storage if present
    const magicLinkValues = getMagicLinkValues();

    // Initialize the state: defaults < local storage < magic link
    const {
        uuid,
        isAuthed,
        garageCreated,
        isDrawerOpen = false,
    } = {
        uuid: undefined,
        isAuthed: false,
        garageCreated: false,
        ...getLocalDealState({ slug: accountSlug, env }),
        ...(magicLinkValues.uuid && {
            uuid: magicLinkValues.uuid,
            garageCreated: true,
            isAuthed: true,
        }),
        ...setDrawerState(magicLinkValues.initialEntries),
    };

    // Setup redux store
    const store = fillStore({
        onlineShopper: {
            ...onlineShopperInitialState,
            env,
            srpLink,
            referer: window.location.href,
            pageType: getPageType(),
            isAuthed,
            garageCreated,
            isDrawerOpen,
            isAppOpen: !!magicLinkValues.uuid || !!magicLinkValues.vin,
            toggledVin: magicLinkValues.vin,
        },
        // @ts-ignore
        account: {
            loading: true,
            slug: accountSlug,
        },
        deal: {
            uuid,
        },
        messages: {
            ...initialMessageState,
            ...getChatLocalState({ slug: accountSlug, env }),
        },
    });

    // Initialize Bugsnag
    const bugsnagClient = initBugsnag(process.env.APP_VERSION!, store);

    if (process.env.NODE_ENV !== "development" && window.location.hostname !== "localhost") {
        datadogRum.init({
            applicationId: "af604ffc-f4b1-4b77-ab30-9d7da4a2f253",
            clientToken: "pubcfdd6a2f7b38f509c6a215a9419c1ffa",
            site: "us5.datadoghq.com",
            service: "electric-v1",
            env: document.getElementById("_di-onlineshopper-loader")?.getAttribute("data-env") || "dev",
            version: process.env.APP_VERSION || "unknown",
            trackViewsManually: true,
            trackResources: false,
            allowedTracingUrls: [/api.onlineshopper/gi],
            beforeSend: (evt: RumEvent) => {
                if (evt.type === "error") return false;
                return true;
            },
        });
    }

    // Render the app using a helper that will handle hot reloading
    render({ store, ErrorBoundary: bugsnagClient, initialEntries: magicLinkValues.initialEntries });

    if (uuid) {
        // Fetch existing deal and patch updated tracking values
        await store.dispatch(getDeal(uuid));
    }

    // Patch updated tracking values, retrying to catch any that haven't loaded yet
    handleHostTrackingData(
        store.dispatch,
        () => ({ uuid: store?.getState()?.deal?.uuid, isAppOpen: !!store?.getState()?.onlineShopper?.isAppOpen }),
        ASYNC_TRACKERS,
        getStaticTrackingValues(env, accountSlug),
        PROMISE_TRACKING_VALUES,
    );

    // If any magic links were present, toggle open
    if (magicLinkValues.uuid || magicLinkValues.vin) {
        // If the OnlineShopper_UUID magic link was present, remember deal in localstorage
        if (magicLinkValues.uuid) {
            rememberDealFromMagicLink(accountSlug, env, magicLinkValues.uuid);
        }
        await toggleOS(store, magicLinkValues.vin);
    }

    // Return the initialized Redux store so our other window.OnlineShopper interface methods have access to it
    return store;
};

export default initialize;
